import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { DomSanitizer } from '@angular/platform-browser';
import { SegmentedMessage } from 'sms-segments-calculator';
import {
    GeneralInformationMessageValidationConst,
    PlatformOptionConst,
} from '../../../../core/const';

interface IMaps {
    zoom: number;
    latitude: number;
    longitude: number;
    isDraggable: boolean;
    isDoubleClickZoom: boolean;
}

@Component({
    selector: 'lib-new-campaign-general-information',
    templateUrl: './general-information.component.html',
    styleUrls: ['./general-information.component.scss'],
})
export class GeneralInformationComponent implements OnInit {
    @Output()
    public generalInformation: EventEmitter<{
        parent: FormGroup;
        child: FormGroup;
    }>;

    public form: FormGroup;
    public channelOption: any[];
    public generalInformationForm: FormGroup;
    public isTouchedWAForm: boolean = false;
    public isTouchedSMSForm: boolean = false;
    public isTouchedEmailForm: boolean = false;
    public isShowMore: boolean = false;
    public validationMessages: any;
    public maps: IMaps;
    public twilioResultCounter = {
        segments: 0,
        characters: 0,
        encoding: '',
        fulltext: `Encode - / Chars 0 / Segment 1`,
    };

    @Input() public isReadOnlyMode: boolean;

    constructor(private sanitizer: DomSanitizer) {
        this.generalInformation = new EventEmitter();
    }

    ngOnInit(): void {
        this.channelOption = PlatformOptionConst;
        this.validationMessages = GeneralInformationMessageValidationConst;
        this.maps = {
            zoom: 15,
            latitude: null,
            longitude: null,
            isDraggable: true,
            isDoubleClickZoom: true,
        };

        this.initForm();
        this.initEdit();
        this.initDisable();
    }

    private initForm(): void {
        this.form = new FormGroup({
            id: new FormControl(null),
            campaignName: new FormControl(null, [Validators.required, Validators.maxLength(100)]),
            medium: new FormControl(null, [Validators.required]),
        });

        this.form.valueChanges.subscribe(() => {
            this.isTouchedWAForm = !this.isTouchedWAForm;
            this.isTouchedSMSForm = !this.isTouchedSMSForm;
            this.isTouchedEmailForm = !this.isTouchedEmailForm;

            this.generalInformation.emit({
                parent: this.form,
                child: this.generalInformationForm,
            });
        });
        setInterval(() => {
            this.onCheckTwilioCounter();
        }, 400);
    }

    private initEdit(): void {
        const campaign = localStorage.getItem('telin.campaign')
            ? JSON.parse(localStorage.getItem('telin.campaign'))
            : {};

        if (Object.entries(campaign).length === 0) return;

        this.form.get('id').setValue(campaign?.generalInformation?.id);
        this.form.get('campaignName').setValue(campaign?.generalInformation?.form.campaignName);
        this.form.get('medium').setValue(campaign?.generalInformation?.form.medium);
    }

    private initDisable(): void {
        if (!this.isReadOnlyMode) {
            return;
        }

        this.form.disable();
    }

    public updateGeneralInformationForm(form: FormGroup): void {
        this.generalInformationForm = form;
        this.isShowMore = false;

        this.generalInformation.emit({
            parent: this.form,
            child: this.generalInformationForm,
        });

        if (this.getMedia === 'LOCATION') this.getLocation();
    }

    get getHeader(): string {
        return this.generalInformationForm?.getRawValue()?.from?.name || '';
    }

    get getBodyString(): string {
        const body =
            this.generalInformationForm?.getRawValue()?.text ||
            this.generalInformationForm?.getRawValue()?.template?.body?.text ||
            this.generalInformationForm?.getRawValue()?.body?.text;

        return (
            body
                ?.split(/\\n|\n/)
                .join('<br />')
                .split(/\\t|\t/)
                .join('    ') ?? ''
        );
    }

    get getBody(): any {
        const body =
            this.generalInformationForm?.getRawValue()?.text ||
            this.generalInformationForm?.getRawValue()?.template?.body?.text ||
            this.generalInformationForm?.getRawValue()?.body?.text;

        return this.sanitizer.bypassSecurityTrustHtml(
            this.fixGeneratedHtml(
                body
                    ?.split(/\\n|\n/)
                    .join('<br />')
                    .split(/\\t|\t/)
                    .join('    ') ?? ''
            )
        );
    }

    get getMedia(): string {
        return this.generalInformationForm?.getRawValue()?.media || '';
    }

    get getTemplate(): any {
        return this.generalInformationForm?.getRawValue()?.template || {};
    }

    get getTemplateButton(): any {
        const limit =
            !this.isShowMore && this.getTemplate?.buttons?.length > 3
                ? 2
                : this.getTemplate?.buttons?.length;

        return [...this.getTemplate?.buttons].splice(0, limit) || [];
    }

    get displayFile(): string {
        return (
            (
                this.generalInformationForm?.getRawValue()?.targetURL ||
                this.generalInformationForm?.getRawValue()?.template?.header?.url
            )?.replace('adlsandbox.com', 'adlsandbox.net') ?? ''
        );
    }

    /* LINE */
    get isText(): boolean {
        return this.generalInformationForm?.getRawValue()?.channel === 'TEXT';
    }

    get isJSON(): boolean {
        return this.generalInformationForm?.getRawValue()?.isJSON;
    }

    get getTemplateLine(): any {
        return this.isJSON
            ? JSON.parse(this.generalInformationForm?.getRawValue()?.template?.body?.text)
            : this.generalInformationForm?.getRawValue()?.template?.body?.text;
    }
    /* ./ LINE */

    private getLocation(): void {
        this.maps.latitude = this.generalInformationForm?.getRawValue()?.latitude;
        this.maps.longitude = this.generalInformationForm?.getRawValue()?.longitude;
    }

    private fixGeneratedHtml(htmlString) {
        return htmlString
            .replaceAll(/div src="http/g, 'img src="http')
            .replaceAll(/div src="data:image/g, 'img src="data:image')
            .replaceAll(/Drag and drop your image here/g, '');
    }

    public onCheckTwilioCounter = (): void => {
        const valuesGeneral = this.generalInformationForm?.getRawValue();
        if (valuesGeneral?.channel === 'TEXT') {
            const segmentedMessage = new SegmentedMessage(this.getBodyString);
            const result = {
                segments: segmentedMessage.segmentsCount,
                encoding: segmentedMessage.encodingName,
                characters: segmentedMessage.numberOfUnicodeScalars,
            };

            this.twilioResultCounter = {
                ...result,
                fulltext: `Encode ${result.encoding} / Chars ${result.characters} / Segment ${result.segments}`,
            };
        } else {
            let valueResult = this.getBodyString;
            Array.from(valuesGeneral?.template?.body?.examples || []).map((entry, index) => {
                const placeholder = `{{${entry}}}`;
                const value = valuesGeneral.paramBody[index]?.name;
                valueResult = valueResult.split(placeholder).join(value);
            });

            const segmentedMessage = new SegmentedMessage(valueResult);
            const result = {
                segments: segmentedMessage.segmentsCount,
                encoding: segmentedMessage.encodingName,
                characters: segmentedMessage.numberOfUnicodeScalars,
            };

            this.twilioResultCounter = {
                ...result,
                fulltext: `Encode ${result.encoding} / Chars ${result.characters} / Segment ${result.segments}`,
            };
        }
    };
}
